import { addDoc, Timestamp } from 'firebase/firestore';
import { resultsRef } from './read';
import { processingStatus, resultStatus, ocrStatus } from '../../../../constants/enum';
import { InputFileType } from '../../../../types/input';
import { createResultHistory } from './history';
import { ResultHistoryActionType } from '../../../../types/resultHistory';
import { generateSortKey } from '../../../../utils/sortKey';
import type { ShareScope } from '../../../../types/pattern';

export interface CreateResultRequest {
  patternId: string;
  userId: string;
  userName: string;
  userIconUrl: string | null | undefined;
  files: InputFileType[]; // アップロードされた全ファイル情報
  originalFiles: InputFileType[]; // 元ファイル情報（PDF分割前）
  geminiModel?: number; // 使用したGeminiモデル
  companyId?: string; // 会社ID
  allowAiLearning?: boolean; // AI学習許可フラグ
  fileIndex?: number; // アップロード時のファイル順序（0始まり）
  pageNumber?: number; // PDFページ番号（1始まり）
  createdAt?: Timestamp; // 同一バッチで共通のタイムスタンプを使う場合に指定
  // 代理操作関連
  isProxy?: boolean; // 代理操作フラグ
  operatorId?: string; // 運営スタッフのUID
  operatorRole?: number; // 運営スタッフのロール
  // 担当者（#2642）
  //   - undefined: 呼び出し元が指定していない → userId / userName / userIconUrl をデフォルトとして利用（既存挙動維持）
  //   - null: 担当者を明示的に未設定で保存（#2642 レビューFB）
  //   - 値あり: その値を保存
  assigneeId?: string | null;
  assigneeName?: string | null;
  assigneeIconUrl?: string | null;
  // 共有範囲（#2804 Phase3）: 実行に使ったパターンから継承（スナップショット）
  shareScope?: ShareScope;
  sharedGroupIds?: string[];
  sharedUserIds?: string[]; // 限定共有時の対象担当者UID（#2963）
  ownerUserId?: string;
  /**
   * アップロード経路（#2967。enum: uploadSource）。
   * 仕分けコストの分析と「親の自動仕分けを経たか」の切り分けに使う。
   * Storage パスからも推測できるが、文字列パスへの依存は脆いため明示的に記録する
   */
  uploadSource?: string;
}

export interface CreateResultResponse {
  resultId: string;
}

/**
 * resultレコードを作成
 */
export const createResult = async ({
  patternId,
  uploadSource,
  userId,
  userName,
  userIconUrl,
  files,
  originalFiles,
  geminiModel,
  companyId,
  allowAiLearning = true,
  fileIndex,
  pageNumber,
  createdAt,
  isProxy,
  operatorId,
  operatorRole,
  assigneeId,
  assigneeName,
  assigneeIconUrl,
  shareScope,
  sharedGroupIds,
  sharedUserIds,
  ownerUserId,
}: CreateResultRequest): Promise<CreateResultResponse> => {
  const resultCreatedAt = createdAt || Timestamp.now();
  // 担当者（#2642）
  //   - 引数が undefined: アップロード者本人をデフォルト担当者とする（既存挙動）
  //   - 引数が null: 担当者を明示的に未設定として保存（#2642 レビューFB）
  //   - 引数に値あり: その値を保存
  const isAssigneeExplicitlyNull = assigneeId === null;
  const resolvedAssigneeId = isAssigneeExplicitlyNull ? null : (assigneeId ?? userId);
  const resolvedAssigneeName = isAssigneeExplicitlyNull ? null : (assigneeName ?? userName);
  const resolvedAssigneeIconUrl = isAssigneeExplicitlyNull
    ? null
    : assigneeIconUrl !== undefined
      ? assigneeIconUrl
      : (userIconUrl ?? null);
  const resultData = {
    log_id: null, // Functions側でログ作成時に設定
    processing_status: processingStatus.processing, // 処理中
    status: resultStatus.valid, // 有効
    result_data: {},
    pattern_id: patternId,
    // アップロード経路（#2967）。未指定の呼び出し元があるため任意扱いにする
    ...(uploadSource ? { upload_source: uploadSource } : {}),
    user_id: userId,
    user_name: userName,
    user_icon_url: userIconUrl ?? null,
    // 担当者（#2642）
    assignee_id: resolvedAssigneeId,
    assignee_name: resolvedAssigneeName,
    assignee_icon_url: resolvedAssigneeIconUrl,
    assignee_set_at: isAssigneeExplicitlyNull ? null : resultCreatedAt,
    assignee_set_by: isAssigneeExplicitlyNull ? null : userId,
    files, // アップロードされた全ファイル情報
    original_files: originalFiles, // 元ファイル情報
    ocr_status: ocrStatus.processing, // OCR処理中
    allow_ai_learning: allowAiLearning, // AI学習許可フラグ
    ...(geminiModel !== undefined && { gemini_model: geminiModel }), // Geminiモデル設定を追加
    ...(companyId && { company_id: companyId }), // 会社IDを追加
    ...(fileIndex !== undefined && { file_index: fileIndex }), // ファイル順序
    ...(pageNumber !== undefined && { page_number: pageNumber }), // ページ番号
    sort_key: generateSortKey(resultCreatedAt, fileIndex ?? 0, pageNumber ?? 1),
    // 代理操作関連
    ...(isProxy !== undefined && { is_proxy: isProxy }),
    ...(operatorId && { operator_id: operatorId }),
    ...(operatorRole !== undefined && { operator_role: operatorRole }),
    // 共有範囲（#2804 Phase3）: 実行に使ったパターンから継承。欠損時は閲覧判定で company 扱い
    ...(shareScope !== undefined && { share_scope: shareScope }),
    ...(sharedGroupIds !== undefined && { shared_group_ids: sharedGroupIds }),
    ...(sharedUserIds !== undefined && { shared_user_ids: sharedUserIds }),
    ...(ownerUserId !== undefined && { owner_user_id: ownerUserId }),
    created_at: resultCreatedAt,
    updated_at: resultCreatedAt,
    created_by: userId,
    updated_by: userId,
  };

  try {
    const docRef = await addDoc(resultsRef, resultData);

    // 履歴記録: 作成（失敗してもメイン処理は継続）
    try {
      await createResultHistory({
        result_id: docRef.id,
        action_type: ResultHistoryActionType.CREATE,
        user_id: userId,
        user_name: userName,
      });
    } catch (historyError) {
      // 履歴記録の失敗はメイン処理を止めない
    }

    return {
      resultId: docRef.id,
    };
  } catch (error) {
    throw new Error('AI読取に失敗しました');
  }
};
